import React from "react";
import Box from "@mui/material/Box";

import EdgeMentorMessage, {
  useEdgeMentor,
} from "./EdgeMentorMessage";
import { useEdgeMentorEnabled } from "./EdgeMentorToggle";
import { registerEdgeMentorModule } from "./edgeMentorModuleRegistry";
import { edgeMentorDiagnostic } from "./edgeMentorDiagnostics";

const DEFAULT_RESOLVER = (fieldName) => fieldName;

const NON_TEXT_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "button",
  "submit",
  "reset",
  "file",
  "hidden",
]);

// Routing only. Displayed tab content still comes from Excel -> JSON.
const PV_INPUT_DATA_TAB_CODE_LABELS = {
  Design: "pvInputDataTabDesign",
  Shell: "pvInputDataTabShell",
  Nozzles: "pvInputDataTabNozzles",
  Attachments: "pvInputDataTabAttachments",
  "2D": "pvInputDataTab2D",
  Spec: "pvInputDataTabSpec",
  Tables: "pvInputDataTabTables",
};

// Legacy/native PV field names that do not pass through the Design registry.
// This is routing only; message content remains entirely in Excel -> JSON.
const PV_LEGACY_FIELD_CODE_LABELS = {
  TxtBx_A_1: "saddleSupportDistanceFromRef",
  TxtBx_DisBtwnSdls: "saddleDistanceBetweenSaddles",
  TxtBx_VOD: "saddleVesselOuterDiameter",
  TxtBx_Wb_CntrLin: "saddleCenterlineToBasePlate",
  TxtBx_Wb_CntctAngl: "saddleContactAngle",
  TxtBx_Wb_Thck: "saddleThickness",
};

const normalizeTabText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const normalizeTableHeader = (value) =>
  String(value ?? "")
    .replace(/²/g, "2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");


const getHumanUiLabel = (target, control = null) => {
  if (!target || typeof target.closest !== "function") {
    return "";
  }

  const actualControl =
    control ||
    target.closest(
      "[data-edge-mentor], input, textarea, [role='combobox'], button",
    );

  // 1. Explicit accessible label on the control.
  const ariaLabel = normalizeTabText(
    actualControl?.getAttribute?.("aria-label") || "",
  );

  if (ariaLabel) {
    return ariaLabel;
  }

  // 2. Native/MUI label linked by id.
  const controlId = String(
    actualControl?.id || "",
  ).trim();

  if (controlId && typeof document !== "undefined") {
    const escapedId =
      typeof CSS !== "undefined" && CSS.escape
        ? CSS.escape(controlId)
        : controlId.replace(/(["\\])/g, "\\$1");

    const linkedLabel = document.querySelector(
      `label[for="${escapedId}"]`,
    );

    const linkedText = normalizeTabText(
      linkedLabel?.textContent || "",
    );

    if (linkedText) {
      return linkedText;
    }
  }

  // 3. MUI FormControl/InputLabel.
  const formControl = actualControl?.closest?.(
    ".MuiFormControl-root",
  );

  const muiLabel = normalizeTabText(
    formControl?.querySelector?.("label")?.textContent || "",
  );

  if (muiLabel) {
    return muiLabel;
  }

  // 4. Existing Design/Saddle two-column rows.
  const designRow = target.closest("#inner-container");

  if (designRow) {
    const rowChildren = Array.from(designRow.children || []);

    for (const child of rowChildren) {
      if (actualControl && child.contains(actualControl)) {
        continue;
      }

      const text = normalizeTabText(child.textContent);

      if (text) {
        return text;
      }
    }
  }

  // 5. MRT/DataGrid cell: use the visible column header.
  const bodyCell = target.closest("td");

  if (bodyCell) {
    const headerText = getTableHeaderForBodyCell(bodyCell);

    if (headerText) {
      return headerText;
    }
  }

  // 6. Tab text.
  const tabControl = target.closest('[role="tab"]');

  if (tabControl) {
    const tabText = normalizeTabText(tabControl.textContent);

    if (tabText) {
      return tabText;
    }
  }

  return "";
};

// Nozzle Input + Geometry + Nozzle Cal + Estimation.
// These keys are normalized from the visible MRT header text.
const NOZZLE_TABLE_HEADER_CODE_LABELS = {
  // Input DataGridView1
  uid: "nozzleUID",
  mark: "nozzleMark",
  service: "nozzleService",
  size: "nozzleSize",
  type: "nozzleType",
  flangerating: "nozzleFlangeRating",
  schthk: "nozzleScheduleThickness",
  welddetail: "nozzleWeldDetail",
  nozzlewelda: "nozzleWeldA",
  nozzleweldamm: "nozzleWeldA",
  nozzleweldain: "nozzleWeldA",
  insideweldb: "nozzleInsideWeldB",
  insideweldbmm: "nozzleInsideWeldB",
  insideweldbin: "nozzleInsideWeldB",
  padweldc: "nozzlePadWeldC",
  padweldcmm: "nozzlePadWeldC",
  padweldcin: "nozzlePadWeldC",
  outsideproj: "nozzleOutsideProjection",
  outsideprojmm: "nozzleOutsideProjection",
  outsideprojin: "nozzleOutsideProjection",
  insideproj: "nozzleInsideProjection",
  insideprojmm: "nozzleInsideProjection",
  insideprojin: "nozzleInsideProjection",
  padod: "nozzlePadOD",
  padodmm: "nozzlePadOD",
  padodin: "nozzlePadOD",
  padthk: "nozzlePadThickness",
  padthkmm: "nozzlePadThickness",
  padthkin: "nozzlePadThickness",

  // Geometry DataGridView10
  distfromdl: "nozzleDistanceFromDatumLine",
  distfromdlmm: "nozzleDistanceFromDatumLine",
  distfromdlin: "nozzleDistanceFromDatumLine",
  orientationdeg: "nozzleOrientation",
  orientation: "nozzleOrientation",
  distfromcl: "nozzleDistanceFromCenterline",
  distfromclmm: "nozzleDistanceFromCenterline",
  distfromclin: "nozzleDistanceFromCenterline",
  dist: "nozzleDistanceFromCenterline",
  distmm: "nozzleDistanceFromCenterline",
  distin: "nozzleDistanceFromCenterline",
  direction: "nozzleDirection",
  location: "nozzleLocation",
  nozzlematerial: "nozzleGeometryNozzleMaterial",
  flangematerial: "nozzleGeometryFlangeMaterial",
  padmaterial: "nozzleGeometryPadMaterial",

  // Nozzle Cal / DataGridView3
  nozzlereqdthk: "nozzleResultRequiredThickness",
  nozzlereqdthkmm: "nozzleResultRequiredThickness",
  areareqd: "nozzleResultAreaRequired",
  areareqdmm2: "nozzleResultAreaRequired",
  areaavailable: "nozzleResultAreaAvailable",
  areaavailablemm2: "nozzleResultAreaAvailable",
  nozzleweldareqdmm: "nozzleResultWeldARequired",
  nozzleweldareqd: "nozzleResultWeldARequired",
  nozzleweldbreqdmm: "nozzleResultWeldBRequired",
  nozzleweldbreqd: "nozzleResultWeldBRequired",
  nozzleweldcreqdmm: "nozzleResultWeldCRequired",
  nozzleweldcreqd: "nozzleResultWeldCRequired",
  flangemawp: "nozzleResultFlangeMawp",
  flangemawpnmm2: "nozzleResultFlangeMawp",
  nozzlemdmt: "nozzleResultNozzleMdmt",
  nozzlemdmtdegc: "nozzleResultNozzleMdmt",
  flangemdmt: "nozzleResultFlangeMdmt",
  flangemdmtdegc: "nozzleResultFlangeMdmt",
  padmdmt: "nozzleResultPadMdmt",
  padmdmtdegc: "nozzleResultPadMdmt",

  // Estimation / DataGridView17
  flangeweight: "nozzleEstFlangeWeight",
  flangeweightkg: "nozzleEstFlangeWeight",
  pipeweight: "nozzleEstPipeWeight",
  pipeweightkg: "nozzleEstPipeWeight",
  padweight: "nozzleEstPadWeight",
  padweightkg: "nozzleEstPadWeight",
  nozzleweldhours: "nozzleEstNozzleWeldHours",
  pipeweldhours: "nozzleEstPipeWeldHours",
  padweldhours: "nozzleEstPadWeldHours",
  boltuphours: "nozzleEstBoltUpHours",
  ndtcost: "nozzleEstNdtCost",
};

// Attachments / Saddle estimation DataGridView13.
const ATTACHMENTS_TABLE_HEADER_CODE_LABELS = {
  item: "saddleEstItem",
  qty: "saddleEstQty",
  weight: "saddleEstWeight",
  materialcostkg: "saddleEstMaterialCostPerKg",
  manhours: "saddleEstManHours",
  materialcost: "saddleEstMaterialCost",
};

const PV_TABLE_HEADER_CODE_LABELS = {
  Nozzles: NOZZLE_TABLE_HEADER_CODE_LABELS,
  Attachments: ATTACHMENTS_TABLE_HEADER_CODE_LABELS,
};

const getSelectedPvInputDataTab = (root) => {
  if (!root) return "";

  const selectedTabs = Array.from(
    root.querySelectorAll('[role="tab"][aria-selected="true"]'),
  );

  for (const tab of selectedTabs) {
    const label = normalizeTabText(tab.textContent);

    if (PV_INPUT_DATA_TAB_CODE_LABELS[label]) {
      return label;
    }
  }

  return "";
};

const getTableHeaderForBodyCell = (bodyCell) => {
  if (!bodyCell) return "";

  const table = bodyCell.closest("table");
  if (!table) return "";

  const bodyRow = bodyCell.parentElement;
  if (!bodyRow) return "";

  const fallbackBodyCells = Array.from(bodyRow.children).filter((element) =>
    element.matches("td, th"),
  );

  const cellIndex =
    Number.isInteger(bodyCell.cellIndex) && bodyCell.cellIndex >= 0
      ? bodyCell.cellIndex
      : fallbackBodyCells.indexOf(bodyCell);

  if (cellIndex < 0) return "";

  const headerRows = Array.from(table.querySelectorAll("thead tr")).reverse();

  for (const headerRow of headerRows) {
    const headerCells = Array.from(headerRow.children).filter((element) =>
      element.matches("th, td"),
    );

    const headerCell = headerCells[cellIndex];

    if (!headerCell) continue;

    const headerText = normalizeTabText(headerCell.textContent);

    if (headerText) {
      return headerText;
    }
  }

  return "";
};

const getNormalizedTableHeaders = (table) => {
  if (!table) return [];

  return Array.from(table.querySelectorAll("thead th"))
    .map((headerCell) => normalizeTableHeader(headerCell.textContent))
    .filter(Boolean);
};

const ATTACHMENTS_ESTIMATION_HEADER_SIGNATURE = [
  "item",
  "qty",
  "weight",
  "materialcostkg",
  "manhours",
  "materialcost",
];

const isAttachmentsEstimationTable = (table) => {
  const headers = new Set(getNormalizedTableHeaders(table));

  return ATTACHMENTS_ESTIMATION_HEADER_SIGNATURE.every((header) =>
    headers.has(header),
  );
};

const getPvTableCellCodeLabel = (target, root) => {
  if (!target || typeof target.closest !== "function") {
    return "";
  }

  const bodyCell = target.closest("td");

  if (!bodyCell) {
    return "";
  }

  const table = bodyCell.closest("table");
  const headerText = getTableHeaderForBodyCell(bodyCell);
  const normalizedHeader = normalizeTableHeader(headerText);

  if (!normalizedHeader) {
    return "";
  }

  // The Saddle estimation grid is nested under Attachments/Saddle.
  // Identify it by its exact six-column signature so nested selected tabs
  // cannot prevent the Edge Mentor routing.
  if (isAttachmentsEstimationTable(table)) {
    return String(
      ATTACHMENTS_TABLE_HEADER_CODE_LABELS[normalizedHeader] || "",
    ).trim();
  }

  const activeDataTab = getSelectedPvInputDataTab(root);
  const headerMap = PV_TABLE_HEADER_CODE_LABELS[activeDataTab];

  if (headerMap?.[normalizedHeader]) {
    return String(headerMap[normalizedHeader]).trim();
  }

  // Safe fallback: use a header mapping only when it resolves uniquely.
  const candidates = Array.from(
    new Set(
      Object.values(PV_TABLE_HEADER_CODE_LABELS)
        .map((map) => map?.[normalizedHeader])
        .filter(Boolean),
    ),
  );

  return candidates.length === 1 ? String(candidates[0]).trim() : "";
};

/**
 * Shared Edge Mentor host.
 *
 * Existing field/parameter behavior is preserved.
 *
 * Additions in this version:
 * - PV Input Data tab messages still resolve through Excel -> JSON.
 * - Nozzle and Attachments MRT cells resolve by their visible column header.
 * - Saddle native field names are routed to their semantic Excel code labels.
 *
 * No table editability, button behavior, calculation logic, estimation logic,
 * tab routing, or modeling logic is changed.
 */
const EdgeMentorHost = ({
  children,
  resolveCodeLabel = DEFAULT_RESOLVER,
  moduleKey = "pv",
  moduleName = "Pressure Vessel",
}) => {
  const {
    entry,
    messageVersion,
    showForCodeLabel,
    closeMessage,
  } = useEdgeMentor(moduleKey);

  const hostRef = React.useRef(null);
  const lastObservedTabCodeRef = React.useRef("");

  // PV has its own independent Edge Mentor ON/OFF state.
  // The shared hook uses the same existing storage key:
  // edgeMentor.pv.enabled
  //
  // Future modules can reuse the same hook with:
  // useEdgeMentorEnabled("piping")
  // useEdgeMentorEnabled("structural")
  // useEdgeMentorEnabled("tank")
  const {
    enabled: edgeMentorEnabled,
  } = useEdgeMentorEnabled(
    moduleKey,
    true,
  );

  // Register the currently mounted module with the global navbar icon.
  // Current PV callers need no change because the defaults above are PV.
  // Future module hosts can register e.g. "piping" or "structural".
  React.useEffect(
    () =>
      registerEdgeMentorModule(
        moduleKey,
        moduleName,
      ),
    [moduleKey, moduleName],
  );

  // The ON/OFF control now lives outside this React subtree in the global
  // navbar. The shared hook synchronizes its state back here immediately.
  React.useEffect(() => {
    if (edgeMentorEnabled) {
      return;
    }

    // OFF immediately closes any current message/tutorial video and clears
    // the observed-tab marker so ON can show the current tab again.
    lastObservedTabCodeRef.current = "";
    closeMessage();
  }, [edgeMentorEnabled, closeMessage]);

  const getTabCodeLabel = React.useCallback((target) => {
    if (!target || typeof target.closest !== "function") {
      return "";
    }

    const tabControl = target.closest('[role="tab"]');

    if (!tabControl) {
      return "";
    }

    const tabLabel = normalizeTabText(tabControl.textContent);

    return String(
      PV_INPUT_DATA_TAB_CODE_LABELS[tabLabel] || "",
    ).trim();
  }, []);

  const handlePointerCapture = React.useCallback(
    (event) => {
      const target = event?.target;

      if (!target || typeof target.closest !== "function") {
        return;
      }

      // The PV toggle itself never opens an Edge Mentor message.
      if (target.closest("[data-edge-mentor-toggle]")) {
        return;
      }

      // PV Edge Mentor OFF = no field, table, tab, or video messages.
      if (!edgeMentorEnabled) {
        return;
      }

      // =====================================================
      // PV INPUT DATA TAB GUIDANCE
      // Content is resolved from Excel -> JSON by code_label.
      // =====================================================
      const tabCodeLabel = getTabCodeLabel(target);

      if (tabCodeLabel) {
        const tabLabel = normalizeTabText(
          target.closest('[role="tab"]')?.textContent || "",
        );

        lastObservedTabCodeRef.current = tabCodeLabel;
        void showForCodeLabel(
          tabCodeLabel,
          tabLabel || tabCodeLabel,
        );
        return;
      }

      let rawFieldName = "";

      // =====================================================
      // GENERIC MARKER
      // Existing field behavior remains unchanged.
      // =====================================================
      const markedControl = target.closest("[data-edge-mentor]");

      if (markedControl) {
        rawFieldName = String(
          markedControl.getAttribute("data-edge-mentor") || "",
        ).trim();
      }

      // =====================================================
      // NOZZLES / ATTACHMENTS MRT CELL FALLBACK
      // Explicit data-edge-mentor markers always win first.
      // This adds read-only + editable table coverage without
      // modifying any Nozzle/Saddle table component.
      // =====================================================
      if (!rawFieldName) {
        const tableCellCodeLabel = getPvTableCellCodeLabel(
          target,
          hostRef.current,
        );

        if (tableCellCodeLabel) {
          const tableFieldLabel =
            getHumanUiLabel(target) || tableCellCodeLabel;

          void showForCodeLabel(
            tableCellCodeLabel,
            tableFieldLabel,
          );
          return;
        }
      }

      // =====================================================
      // TEXTBOX COMPATIBILITY FALLBACK
      // Existing field behavior remains unchanged.
      // =====================================================
      if (!rawFieldName) {
        const input = target.closest("input, textarea");

        if (!input) {
          return;
        }

        const inputType = String(input.type || "text").toLowerCase();

        if (NON_TEXT_INPUT_TYPES.has(inputType)) {
          return;
        }

        rawFieldName = String(
          input.name || input.id || "",
        ).trim();
      }

      if (!rawFieldName) {
        return;
      }

      const uiFieldLabel =
        getHumanUiLabel(target, markedControl) ||
        rawFieldName;

      // Saddle inputs use native names that are outside the Design registry.
      // Other PV fields continue through the existing resolver unchanged.
      const designRowText = normalizeTabText(
        target.closest("#inner-container")?.textContent || "",
      ).toLowerCase();

      // The current Design data has two visible seismic rows that can share
      // the legacy field name seismicZone. Keep the form/business key exactly
      // as it is, but route the visible Seismic Spec row to its own mentor
      // content entry.
      const designControlCodeLabel =
        rawFieldName === "seismicZone" &&
        (designRowText.includes("seismic spec") ||
          designRowText.includes("seismic design code"))
          ? "seismicDesignCode"
          : "";

      const legacyCodeLabel = String(
        PV_LEGACY_FIELD_CODE_LABELS[rawFieldName] || "",
      ).trim();

      const resolvedCodeLabel = designControlCodeLabel
        ? designControlCodeLabel
        : legacyCodeLabel
          ? legacyCodeLabel
          : String(resolveCodeLabel(rawFieldName) || "").trim();

      if (!resolvedCodeLabel) {
        edgeMentorDiagnostic("unresolved-control", {
          a: uiFieldLabel,
        });
        closeMessage();
        return;
      }

      void showForCodeLabel(
        resolvedCodeLabel,
        uiFieldLabel,
      );
    },
    [
      resolveCodeLabel,
      showForCodeLabel,
      closeMessage,
      getTabCodeLabel,
      edgeMentorEnabled,
    ],
  );

  /**
   * Show the tab guidance when a known PV Input Data tab becomes selected
   * without a direct pointer click (initial selection or programmatic change).
   *
   * This observes existing MUI aria-selected state only. It does not change
   * NavTabs/DataTabs routing or tab behavior.
   */
  React.useEffect(() => {
    const root = hostRef.current;

    if (
      !edgeMentorEnabled ||
      !root ||
      typeof MutationObserver === "undefined"
    ) {
      return undefined;
    }

    let initialTimer = null;

    const syncSelectedTab = () => {
      const selectedTabs = Array.from(
        root.querySelectorAll('[role="tab"][aria-selected="true"]'),
      );

      for (const tabElement of selectedTabs) {
        const tabLabel = normalizeTabText(tabElement.textContent);
        const codeLabel = String(
          PV_INPUT_DATA_TAB_CODE_LABELS[tabLabel] || "",
        ).trim();

        if (!codeLabel) {
          continue;
        }

        if (lastObservedTabCodeRef.current === codeLabel) {
          return;
        }

        lastObservedTabCodeRef.current = codeLabel;
        void showForCodeLabel(
          codeLabel,
          tabLabel || codeLabel,
        );
        return;
      }
    };

    initialTimer = window.setTimeout(syncSelectedTab, 0);

    const observer = new MutationObserver((mutations) => {
      const selectionChanged = mutations.some(
        (mutation) =>
          (mutation.type === "attributes" &&
            mutation.attributeName === "aria-selected") ||
          mutation.type === "childList",
      );

      if (selectionChanged) {
        syncSelectedTab();
      }
    });

    observer.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["aria-selected"],
    });

    return () => {
      if (initialTimer) {
        window.clearTimeout(initialTimer);
      }

      observer.disconnect();
    };
  }, [showForCodeLabel, edgeMentorEnabled]);

  return (
    <>
      <Box
        ref={hostRef}
        onMouseDownCapture={handlePointerCapture}
        sx={{ display: "contents" }}
      >
        {children}
      </Box>

      {edgeMentorEnabled && (
        <EdgeMentorMessage
          entry={entry}
          messageVersion={messageVersion}
          onClose={closeMessage}
        />
      )}
    </>
  );
};

export default EdgeMentorHost;
